import MaterialMenu from '@material-ui/core/Menu'
import MaterialMenuItem from '@material-ui/core/MenuItem'
import * as d3 from 'd3-hierarchy'
import React, { useCallback, useState } from 'react'

export interface IValueType {
  id: string
  parentId: string | null
  value: string | number
  text: React.ReactNode
  isDisabled?: boolean
  style?: any
}

interface IPropsType<Value extends IValueType> {
  forwardRef?: React.Ref<HTMLLIElement> | null
  prevValue?: Value['value'][]
  onSelect: (value: Value['value'][]) => void
  onClose: () => void
  option: d3.HierarchyNode<Value>
  customizedMuiClasses?: any
}

const withForwardRef = <Value extends IValueType>(
  Compoment: typeof MenuItem,
): typeof MenuItem => {
  const MemoComponent = React.memo(Compoment) as unknown as typeof MenuItem

  return React.forwardRef<HTMLLIElement, IPropsType<Value>>((props, ref) => (
    <MemoComponent {...props} forwardRef={ref} />
  )) as unknown as typeof MenuItem
}

const MenuItem = <Value extends IValueType>({
  forwardRef,
  prevValue = [],
  onSelect,
  onClose: prevOnClose,
  option: {
    data: { value, text, isDisabled, style },
    children,
  },
  customizedMuiClasses,
}: IPropsType<Value>) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const onClose = useCallback(() => {
    prevOnClose()
    setAnchorEl(null)
  }, [prevOnClose])
  const SubMenuItem = withForwardRef(MenuItem)

  const handleSubMenuClose = () => {
    if (anchorEl) {
      setAnchorEl(null)
    }
  }

  return (
    <>
      <MaterialMenuItem
        ref={forwardRef}
        className={`${customizedMuiClasses?.MenuItem || ''} ${
          children ? '' : 'is-leaf'
        }`}
        value={value}
        onClick={({ target }) => {
          if (!children) {
            onSelect([...prevValue, value])
            onClose()
          } else {
            // @ts-ignore: FIXME types error
            setAnchorEl((target || null) as any)
          }
        }}
        disabled={isDisabled}
        style={style ?? {}}
      >
        {text}
      </MaterialMenuItem>

      {!children ? null : (
        <MaterialMenu
          className={customizedMuiClasses?.Menu}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleSubMenuClose}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          variant="menu"
        >
          {children.map((option) => (
            <SubMenuItem
              key={option.id}
              prevValue={[...prevValue, value]}
              onSelect={onSelect}
              onClose={onClose}
              option={option}
              customizedMuiClasses={customizedMuiClasses}
            />
          ))}
        </MaterialMenu>
      )}
    </>
  )
}

export default withForwardRef(MenuItem)
